import React, { useEffect, useRef, useState } from 'react'
import ChallengeSpaceService from '../../../services/challenge-space.service'
import Select from 'react-select'
import FoldableComponent from '../shared/FoldableComponent'
import TeamFormationTeamRequirementControl from './TeamFormationTeamRequirementControl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import confirmService from '../../../services/confirm.service'
import { useSelector } from 'react-redux'
import { isAllowed } from '../../../utils/helpers'
import Tippy from '@tippyjs/react'

interface TeamFormationTeamRequirementsFormProps {
	challengeSpaceId: string
	challengeId: string
	requirements: any[]
	viewOnly?: boolean
}

const TeamFormationTeamRequirementsForm: React.FunctionComponent<TeamFormationTeamRequirementsFormProps> =
	({
		challengeSpaceId,
		challengeId,
		requirements,
		viewOnly = false,
	}: TeamFormationTeamRequirementsFormProps) => {
		const [questions, setQuestions] = useState<any[]>([])
		const [selectedQuestion, setSelectedQuestion] = useState<any>(null)
		const [loading, setLoading] = useState<boolean>(false)
		const [fallbackMessage, setFallbackMessage] = useState<string>('')
		const [requirementList, setRequirementList] = useState<any>([])
		const [teamFormation, setTeamFormation] = useState<any>(null)

		const user = useSelector((state) => state['auth'].user)

		const requirementsRef = useRef([])

		useEffect(() => {
			requirementsRef.current = requirementsRef.current.slice(
				0,
				requirementList.length
			)
		}, [requirementList])

		const handleSelectChange = (value) => {
			setSelectedQuestion(value)
		}

		const addRequirement = () => {
			const newReq = {
				question: selectedQuestion,
				options: selectedQuestion.attributes.options?.items?.map((o) => {
					return { ...o, maxSlots: 0 }
				}),
			}
			setRequirementList([...requirementList, newReq])
			// Re-set selected question after add
			setSelectedQuestion(null)
		}

		const getQuestionsFiltered = () => {
			const mappedRequirementList = requirementList.map((r) => r.question.id)
			return questions.filter((q) => mappedRequirementList.indexOf(q.id) < 0)
		}

		const handleRemove = (e, item: any, index: number) => {
			e.stopPropagation()
			confirmService
				.confirm({
					title: `Are you sure you want to remove this requirement?`,
					confirmButtonText: `Yes! Remove`,
					customClass: {
						confirmButton: `bg-red-600 text-white mr-4`,
						cancelButton: 'bg-gray-200 text-gray-800',
					},
				})
				.then(async (result) => {
					if (result.value) {
						// Todo: call endpoint to remove requirement
						try {
							if (item.id) {
								await requirementsRef.current[index].removeAllRequirements(item)
							}
							setRequirementList([
								...requirementList.filter(
									(r) => r.question.id !== item.question.id
								),
							])
						} catch (e) {
							console.log(e)
						}
					} else {
						return 'cancel'
					}
				})
		}

		const HeaderCompSlot = (item, index: number) => (
			<button
				type="button"
				onClick={(e) => handleRemove(e, item, index)}
				className="text-red-600 focus:ring-0 p-2 rounded-0"
			>
				<FontAwesomeIcon icon={faTrashAlt} />
			</button>
		)

		const updateRequirementsFromStore = (reqList, questionList) => {
			setRequirementList([
				...reqList.map((r) => {
					const question = questionList.find(
						(q) => q.id === r.attributes.questionId
					)
					return {
						question,
						id: r.id,
						options: question?.attributes?.options?.items?.map((item) => {
							const findOpt = r.attributes.options.find(
								(o) => o.answerItemId === item.id
							)
							return {
								...item,
								maxSlots: findOpt ? findOpt.maxSlots : 0,
								answerItemId: findOpt ? findOpt.answerItemId : item.id,
							}
						}),
					}
				}),
				...requirementList.filter(
					(item) =>
						reqList
							.map((r) => r.attributes.questionId)
							.indexOf(item.question.id) < 0
				),
			])
		}

		const isEditEnabled = () => {
			return (
				teamFormation.attributes.state !== 'finalized' &&
				isAllowed(user, ['space-admin', 'organisation-admin', 'admin']) &&
				!viewOnly
			)
		}

		useEffect(() => {
			const abortCtrl = new AbortController()
			const getTeamRequirements = async () => {
				setLoading(true)

				try {
					const teamFormationConfig: {
						meta: any
						teamFormation: { [key: string]: any }
						teamFormationQuestion: { [key: string]: any }
					} = await ChallengeSpaceService.getChallengeSpaceTeamFormation(
						challengeSpaceId
					)
					// Set team formation id for use in control components
					const tf = Object.values(teamFormationConfig.teamFormation).length
						? Object.values(teamFormationConfig.teamFormation)[0]
						: null

					setTeamFormation(tf)

					// Set available questions:
					// Questions must be:
					// - a select type (single)
					// - and have selectable items (options)
					const questionRes = Object.values(
						teamFormationConfig?.teamFormationQuestion
					).filter(
						(q: any) =>
							q.attributes.type.includes('select') &&
							q.attributes.options?.items?.length > 0
					)
					setQuestions(questionRes)

					// Set existing requirements from parent after questions are loaded
					if (requirements.length > 0) {
						updateRequirementsFromStore(requirements, questionRes)
					}
				} catch (error: any) {
					if (error.name === 'AbortError') return
					setFallbackMessage('No team requirements yet.')
				}
				setLoading(false)
			}
			getTeamRequirements()
			return () => abortCtrl.abort()
		}, [])

		useEffect(() => {
			if (loading) {
				return
			}
			updateRequirementsFromStore(requirements, questions)
		}, [requirements])

		return (
			<div>
				{loading ? (
					'loading..'
				) : (
					<div className="flex flex-col pb-6">
						{fallbackMessage ? (
							fallbackMessage
						) : (
							<div>
								<h3 className="text-lg font-semibold mb-2">
									Team requirements{' '}
									<Tippy
										theme="light"
										interactive={true}
										placement="bottom"
										content={
											<div className="text-left font-normal p-1">
												Team requirements are used in the{' '}
												<strong>Team Formation</strong> phase to set extra
												requirements for the team that is matched with this
												challenge (ideally)
											</div>
										}
									>
										<a href="#">
											<FontAwesomeIcon
												className="ml-1 text-purple-700"
												icon={faInfoCircle}
											/>
										</a>
									</Tippy>
								</h3>
								{teamFormation && (
									<div>
										{requirementList.length > 0 ? (
											requirementList.map((r, index) => {
												return (
													<div key={r.question.id} className="mb-2">
														<FoldableComponent
															title={r.question.attributes.title}
															headerComp={
																isEditEnabled()
																	? HeaderCompSlot(r, index)
																	: null
															}
															initialOpen={true}
														>
															<TeamFormationTeamRequirementControl
																ref={(el) =>
																	(requirementsRef.current[index] = el)
																}
																teamFormationId={teamFormation.id}
																editEnabled={isEditEnabled()}
																challengeId={challengeId}
																requirement={r}
															/>
														</FoldableComponent>
													</div>
												)
											})
										) : (
											<p className="text-gray-700">No team requirements yet.</p>
										)}
										{isEditEnabled() && (
											<div className="shadow-sm bg-white rounded border p-4 mt-4 flex flex-wrap">
												<Select
													isClearable={false}
													getOptionValue={(option) => option.id}
													getOptionLabel={(opt) => opt.attributes.title}
													value={selectedQuestion}
													defaultOptions
													options={getQuestionsFiltered()}
													menuPosition={'fixed'}
													instanceId={'questionToAddRequirement'}
													className="flex-grow mr-2"
													classNamePrefix="react-select"
													isSearchable={true}
													styles={{
														input: (provided) => ({
															...provided,
															'input:focus': {
																boxShadow: 'none',
															},
														}),
													}}
													onChange={handleSelectChange}
													placeholder="Select an question"
												/>
												<button
													className="button bg-green-500 text-white text-sm"
													disabled={!selectedQuestion}
													onClick={addRequirement}
												>
													Add
												</button>
											</div>
										)}
									</div>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		)
	}

export default TeamFormationTeamRequirementsForm
